import { useState } from 'react'

interface FiltersModalState {
  statuses?: string[]
  types?: string[]
}

interface UseFiltersModalOptions<T extends FiltersModalState> {
  appliedFilters: T
  onApply: (filters: { statuses: string[]; types: string[] }) => void
  onResetAll: () => void
}

export const useFiltersModal = <T extends FiltersModalState>({
  appliedFilters,
  onApply,
  onResetAll,
}: UseFiltersModalOptions<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [localStatuses, setLocalStatuses] = useState<string[]>(appliedFilters?.statuses ?? [])
  const [localTypes, setLocalTypes] = useState<string[]>(appliedFilters?.types ?? [])

  const toggleStatus = (value: string) => {
    setLocalStatuses(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    )
  }

  const toggleType = (value: string) => {
    setLocalTypes(prev => (prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]))
  }

  const handleOpen = () => {
    setLocalStatuses(appliedFilters?.statuses ?? [])
    setLocalTypes(appliedFilters?.types ?? [])
    setIsOpen(true)
  }

  const handleApply = () => {
    onApply({ statuses: localStatuses, types: localTypes })
    setIsOpen(false)
  }

  const handleReset = () => {
    setLocalStatuses([])
    setLocalTypes([])
    onResetAll()
    setIsOpen(false)
  }

  return {
    isOpen,
    setIsOpen,
    localStatuses,
    localTypes,
    toggleStatus,
    toggleType,
    handleOpen,
    handleApply,
    handleReset,
  }
}
